import { motion, useInView, useReducedMotion } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { ArrowUpRight, Mail, MapPin, Phone, CheckCircle2 } from 'lucide-react';
import Eyebrow from './Eyebrow';
import { api } from '../api';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];

const serviceOptions = ['Custom Software Development', 'UX/UI Design & Product Strategy', 'AI Consulting & Implementation', 'Digital Transformation', 'Data Engineering & Analytics', 'Other'];

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase mb-2" style={{ color: '#6B6B72' }}>
        {label}{required ? ' *' : ''}
      </label>
      {children}
    </div>
  );
}

export default function Contact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const shouldReduce = useReducedMotion();

  const [form, setForm] = useState({ name: '', email: '', company: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState('');
  const [contactInfo, setContactInfo] = useState({ email: 'hello@syenovix.com', phone: '+1 (415) 800-9200', hq: 'San Francisco, CA' });

  useEffect(() => {
    api.getConfig('contact').then(d => { if (d?.email) setContactInfo(d); }).catch(() => {});
  }, []);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.submitEnquiry(form);
      setSubmitted(true);
    } catch {
      // Still show success — don't block UX if API hiccups
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = (name) => ({
    borderColor: focused === name ? ACCENT : 'rgba(24,24,27,0.12)',
    boxShadow: focused === name ? `0 0 0 3px rgba(94,14,215,0.10)` : 'none',
    transition: 'border-color 160ms ease, box-shadow 160ms ease',
    background: '#FFFFFF',
    borderRadius: 12,
    padding: '10px 14px',
    fontSize: 13,
    color: '#18181B',
    width: '100%',
    border: '1px solid',
    outline: 'none',
  });

  return (
    <section id="contact" className="px-5 sm:px-8 md:px-12 lg:px-20 flex flex-col justify-center py-24 section-mesh" style={{ minHeight: '100dvh' }}>
      <div className="max-w-7xl mx-auto">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-16">

          {/* Left */}
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, ease }}>
              <Eyebrow>Let's Talk</Eyebrow>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.08, ease }}
              className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.08] mb-5"
              style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.025em' }}
            >
              Start Your Transformation
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.16, ease }}
              className="text-base leading-relaxed mb-10"
              style={{ color: '#6B6B72' }}
            >
              Tell us about your project. We'll respond within one business day with a proposed approach and a free strategy consultation.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.22, ease }}
              className="flex flex-col gap-4 mb-10"
            >
              {[{ icon: Mail, label: 'Email', value: contactInfo.email }, { icon: Phone, label: 'Phone', value: contactInfo.phone }, { icon: MapPin, label: 'HQ', value: contactInfo.hq }].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-center gap-3.5">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(94,14,215,0.07)' }}>
                    <Icon size={15} style={{ color: ACCENT }} />
                  </div>
                  <div>
                    <div className="text-[10px] font-medium tracking-wider uppercase" style={{ color: '#A0A0A7' }}>{label}</div>
                    <div className="text-sm font-medium mt-0.5" style={{ color: '#18181B' }}>{value}</div>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Free consultation block — double-bezel */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.30, ease }}
            >
              <div className="rounded-[1.5rem] p-[6px]" style={{ background: 'rgba(94,14,215,0.04)', border: '1px solid rgba(94,14,215,0.10)' }}>
                <div className="rounded-[1.2rem] p-6" style={{ background: 'rgba(94,14,215,0.03)' }}>
                  <p className="text-[10px] font-semibold tracking-[0.14em] uppercase mb-2" style={{ color: ACCENT }}>Free Consultation</p>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: '#3f3f46' }}>
                    Book a 45-minute discovery call with one of our principals. No sales pitch — just a focused conversation about your challenges and how we can help.
                  </p>
                  <motion.a
                    href={`mailto:${contactInfo.email}`}
                    whileTap={{ scale: 0.97 }}
                    transition={{ duration: 0.12 }}
                    className="inline-flex items-center gap-1.5 text-[13px] font-semibold tracking-wide"
                    style={{ color: ACCENT }}
                  >
                    Book a Call
                    <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'rgba(94,14,215,0.10)' }}>
                      <ArrowUpRight size={11} style={{ color: ACCENT }} />
                    </div>
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right form */}
          <motion.div
            initial={{ opacity: 0, y: shouldReduce ? 0 : 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.18, ease }}
          >
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease }}
                className="flex flex-col items-center justify-center h-full py-20 text-center"
              >
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, ease, delay: 0.1 }}
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
                  style={{ background: 'rgba(94,14,215,0.08)' }}
                >
                  <CheckCircle2 size={26} style={{ color: ACCENT }} />
                </motion.div>
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Message Sent</h3>
                <p className="text-sm max-w-xs" style={{ color: '#6B6B72' }}>We'll be in touch within one business day. Looking forward to it.</p>
              </motion.div>
            ) : (
              <div className="rounded-[1.75rem] p-[7px]" style={{ background: 'rgba(24,24,27,0.02)', border: '1px solid rgba(24,24,27,0.07)' }}>
                <form
                  onSubmit={handleSubmit}
                  className="rounded-[1.375rem] bg-white p-8 flex flex-col gap-5"
                  style={{ boxShadow: '0 20px 60px -15px rgba(0,0,0,0.06)' }}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Full Name" required>
                      <input type="text" name="name" required value={form.name} onChange={handleChange} placeholder="Jane Doe" style={fieldStyle('name')} onFocus={() => setFocused('name')} onBlur={() => setFocused('')} />
                    </Field>
                    <Field label="Work Email" required>
                      <input type="email" name="email" required value={form.email} onChange={handleChange} placeholder="jane@company.com" style={fieldStyle('email')} onFocus={() => setFocused('email')} onBlur={() => setFocused('')} />
                    </Field>
                  </div>
                  <Field label="Company">
                    <input type="text" name="company" value={form.company} onChange={handleChange} placeholder="Acme Inc." style={fieldStyle('company')} onFocus={() => setFocused('company')} onBlur={() => setFocused('')} />
                  </Field>
                  <Field label="Service Interest">
                    <select name="service" value={form.service} onChange={handleChange} style={{ ...fieldStyle('service'), cursor: 'pointer', appearance: 'none' }} onFocus={() => setFocused('service')} onBlur={() => setFocused('')}>
                      <option value="">Select a service...</option>
                      {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Tell Us About Your Project" required>
                    <textarea name="message" required rows={5} value={form.message} onChange={handleChange} placeholder="Briefly describe your project, goals, and timeline..." style={{ ...fieldStyle('message'), resize: 'none' }} onFocus={() => setFocused('message')} onBlur={() => setFocused('')} />
                  </Field>

                  {/* Button-in-button submit */}
                  <motion.button
                    type="submit"
                    whileTap={{ scale: 0.97 }}
                    transition={{ duration: 0.12 }}
                    disabled={loading}
                    className="flex items-center justify-center gap-2 text-white font-semibold text-[13px] tracking-wider uppercase py-3.5 rounded-xl"
                    style={{ background: ACCENT, opacity: loading ? 0.8 : 1, transition: 'opacity 160ms ease', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {loading ? (
                      <>
                        <motion.span animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }} className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full block" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.18)' }}>
                          <ArrowUpRight size={13} color="#fff" />
                        </div>
                      </>
                    )}
                  </motion.button>
                  <p className="text-[11px] text-center" style={{ color: '#A0A0A7' }}>We typically respond within 1 business day.</p>
                </form>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
